import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UploadCloud, Trash2, ChevronDown, ChevronRight, Loader2, ClipboardCheck, FileSpreadsheet, FileDown } from "lucide-react";
import { BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { toast } from "sonner";

export const OutcomeBadge = ({ r }) => {
  if (!r) return <span className="text-xs text-slate-400">Non sostenuto</span>;
  return r.passed
    ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Superato</span>
    : <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700">Non superato</span>;
};

export const fmtWhen = (r) => (r ? (r.completed || r.started || "").split(/\s+/).join(" ") || "—" : "—");

export function OverallSummary({ s }) {
  if (!s) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-8 gap-y-2 px-4 py-3 border-t border-slate-200 bg-slate-50/60" data-testid="assessment-overall-summary">
      <div><div className="text-[11px] uppercase tracking-wide text-slate-500">Test sostenuti</div><div className="font-bold tabular">{s.taken} su {s.tests}</div></div>
      <div><div className="text-[11px] uppercase tracking-wide text-slate-500">Media complessiva</div><div className="font-bold tabular" data-testid="assessment-overall-avg">{s.avg_pct}%</div></div>
      <div><div className="text-[11px] uppercase tracking-wide text-slate-500">Soglia</div><div className="font-bold tabular">{s.pass_pct}%</div></div>
      <div><div className="text-[11px] uppercase tracking-wide text-slate-500">Esito complessivo</div><OutcomeBadge r={{ passed: s.passed }} /></div>
      {s.taken < s.tests && <div className="text-xs text-amber-700">I test non sostenuti sono conteggiati come 0%.</div>}
    </div>
  );
}

const fmtScore = (r) => (r.score === null || r.score === undefined ? "—" : `${String(r.score).replace(".", ",")} / ${r.max_score}`);
const fmtPct = (v) => (v === null || v === undefined ? "—" : `${v}%`);

function UploadForm({ courseId, onDone }) {
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [pass, setPass] = useState(70);
  const [busy, setBusy] = useState(false);
  const pick = (e) => {
    const f = e.target.files?.[0]; setFile(f || null);
    if (f && !name) {
      const m = f.name.replace(/\.csv$/i, "").replace(/-valutazioni$/i, "").split("-");
      setName((m.length > 1 ? m[m.length - 1] : m[0]).trim());
    }
  };
  const submit = async () => {
    if (!file || !name.trim()) { toast.error("Seleziona il file e indica il nome del test"); return; }
    const fd = new FormData(); fd.append("file", file); fd.append("name", name); fd.append("pass_pct", pass);
    setBusy(true);
    try {
      const { data } = await api.post(`/courses/${courseId}/assessments/import`, fd);
      setFile(null); setName(""); onDone(data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <Card className="p-5 border-slate-200" data-testid="assessment-upload-card">
      <h3 className="font-display font-bold text-slate-900 mb-1 flex items-center gap-2"><UploadCloud size={18} /> Carica risultati test (CSV)</h3>
      <p className="text-xs text-slate-500 mb-4">Formato export valutazioni (Cognome, Nome, Indirizzo email, Stato, Iniziato, Completato, Tempo impiegato, Valutazione/max). Ricaricando un test con lo stesso nome i risultati vengono aggiornati.</p>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_140px_auto] gap-3 items-end">
        <div><Label>File CSV</Label><Input type="file" accept=".csv" onChange={pick} data-testid="assessment-file-input" /></div>
        <div><Label>Nome del test</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. Test intermedio 1" data-testid="assessment-name-input" /></div>
        <div><Label>Soglia superamento %</Label><Input type="number" min={1} max={100} value={pass} onChange={(e) => setPass(e.target.value)} data-testid="assessment-pass-input" /></div>
        <Button onClick={submit} disabled={busy} className="bg-[#008C45] hover:bg-[#006B34]" data-testid="assessment-upload-btn">
          {busy ? <Loader2 size={16} className="mr-1 animate-spin" /> : <UploadCloud size={16} className="mr-1" />} Importa
        </Button>
      </div>
    </Card>
  );
}

function AssessmentCard({ a, onChanged, onDelete }) {
  const [open, setOpen] = useState(false);
  const [pass, setPass] = useState(a.pass_pct);
  useEffect(() => { setPass(a.pass_pct); }, [a.pass_pct]);
  const saveThreshold = async () => {
    try { await api.put(`/assessments/${a.id}`, { pass_pct: Number(pass) }); toast.success("Soglia aggiornata"); onChanged(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const st = a.stats;
  return (
    <Card className="border-slate-200 overflow-hidden" data-testid={`assessment-${a.id}`}>
      <div className="flex flex-wrap items-center gap-4 px-4 py-3 bg-slate-50">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-2 font-semibold text-slate-900" data-testid={`assessment-toggle-${a.id}`}>
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />} {a.name}
        </button>
        <span className="text-xs text-slate-500">{st.count} candidati · punteggio max {a.max_score}</span>
        <span className="text-xs font-semibold text-emerald-700">{st.passed} superati</span>
        <span className="text-xs font-semibold text-rose-700">{st.failed} non superati</span>
        <span className="text-xs text-slate-600">Media {fmtPct(st.avg_pct)}</span>
        <div className="ml-auto flex items-center gap-2">
          <Label className="text-xs text-slate-500">Soglia %</Label>
          <Input type="number" value={pass} onChange={(e) => setPass(e.target.value)} className="w-20 h-8" data-testid={`assessment-threshold-${a.id}`} />
          {Number(pass) !== a.pass_pct && <Button size="sm" variant="outline" onClick={saveThreshold} data-testid={`assessment-threshold-save-${a.id}`}>Applica</Button>}
          <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => onDelete(a)} data-testid={`assessment-delete-${a.id}`}><Trash2 size={15} /></Button>
        </div>
      </div>
      {open && (
        <Table>
          <TableHeader><TableRow>
            <TableHead>Candidato</TableHead><TableHead>Punteggio</TableHead><TableHead>%</TableHead><TableHead>Esito</TableHead>
            <TableHead>Completato</TableHead><TableHead>Tempo</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {a.results.map((r) => (
              <TableRow key={r.enrollment_id}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="tabular">{fmtScore(r)}</TableCell>
                <TableCell className="tabular">{fmtPct(r.pct)}</TableCell>
                <TableCell><OutcomeBadge r={r} /></TableCell>
                <TableCell className="text-xs text-slate-500">{r.completed || "—"}</TableCell>
                <TableCell className="text-xs text-slate-500">{r.duration || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}

function ImportResult({ res, onClose }) {
  return (
    <Dialog open={!!res} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="assessment-import-dialog">
        <DialogHeader><DialogTitle>Riepilogo importazione test</DialogTitle></DialogHeader>
        {res && (
          <div className="space-y-3 text-sm">
            <p><strong>{res.assessment.name}</strong> {res.updated_existing ? "(test aggiornato)" : "(nuovo test)"}</p>
            <p>Righe lette: <strong>{res.attempts}</strong> · Candidati abbinati: <strong data-testid="assessment-matched">{res.matched}</strong></p>
            <p>Superati: <strong className="text-emerald-700">{res.assessment.stats.passed}</strong> · Non superati: <strong className="text-rose-700">{res.assessment.stats.failed}</strong></p>
            {res.unmatched.length > 0 && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 max-h-48 overflow-y-auto" data-testid="assessment-unmatched">
                <div className="text-xs font-semibold text-amber-800 mb-1">Non abbinate (non salvate): {res.unmatched.length}</div>
                {res.unmatched.map((u) => <div key={u} className="text-xs text-amber-800">{u}</div>)}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function OverallSection({ course, version }) {
  const [ov, setOv] = useState(null);
  const [pass, setPass] = useState(70);
  const [dl, setDl] = useState(null);
  const download = async (format) => {
    setDl(format);
    try {
      const r = await api.get(`/courses/${course.id}/assessments/export`, { params: { format }, responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = `verifiche_${course.code}.${format}`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download non riuscito"); }
    setDl(null);
  };
  const load = () => api.get(`/courses/${course.id}/assessments/overview`).then((r) => { setOv(r.data); setPass(r.data.pass_pct); });
  useEffect(() => { load(); }, [course.id, version]); // eslint-disable-line
  if (!ov || ov.tests.length === 0) return null;
  const save = async () => {
    try { await api.put(`/courses/${course.id}/assessment-settings`, { pass_pct: Number(pass) }); toast.success("Soglia complessiva aggiornata"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const chart = ov.rows.map((r) => ({ name: r.name, full: r.name, media: r.avg_pct, passed: r.passed }));
  return (
    <Card className="border-slate-200 overflow-hidden" data-testid="assessment-overall-section">
      <div className="flex flex-wrap items-center gap-4 px-4 py-3 bg-[#0B192C] text-white">
        <span className="font-semibold">Risultato complessivo</span>
        <span className="text-xs text-white/70">Media di {ov.tests.length} test (test non sostenuti = 0%)</span>
        <span className="text-xs font-semibold text-emerald-300" data-testid="overall-passed">{ov.stats.passed} superati</span>
        <span className="text-xs font-semibold text-rose-300" data-testid="overall-failed">{ov.stats.failed} non superati</span>
        <span className="text-xs">Media classe {fmtPct(ov.stats.avg_pct)}</span>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="secondary" disabled={!!dl} onClick={() => download("xlsx")} data-testid="assessments-export-xlsx">{dl === "xlsx" ? <Loader2 size={14} className="mr-1 animate-spin" /> : <FileSpreadsheet size={14} className="mr-1" />} Excel</Button>
          <Button size="sm" variant="secondary" disabled={!!dl} onClick={() => download("pdf")} data-testid="assessments-export-pdf">{dl === "pdf" ? <Loader2 size={14} className="mr-1 animate-spin" /> : <FileDown size={14} className="mr-1" />} PDF</Button>
          <span className="text-xs text-white/70">Soglia complessiva %</span>
          <Input type="number" value={pass} onChange={(e) => setPass(e.target.value)} className="w-20 h-8 bg-white text-slate-900" data-testid="overall-threshold-input" />
          {Number(pass) !== ov.pass_pct && <Button size="sm" variant="secondary" onClick={save} data-testid="overall-threshold-save">Applica</Button>}
        </div>
      </div>
      <div className="p-4" data-testid="overall-chart">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chart} margin={{ left: -20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="name" fontSize={11} stroke="#475569" angle={-40} textAnchor="end" interval={0} />
            <YAxis domain={[0, 100]} fontSize={11} stroke="#94a3b8" unit="%" />
            <Tooltip formatter={(v) => `${v}%`} labelFormatter={(l, p) => p?.[0]?.payload?.full || l} />
            <ReferenceLine y={ov.pass_pct} stroke="#DC2626" strokeDasharray="4 4" />
            <Bar dataKey="media" name="Media" radius={[3, 3, 0, 0]}>
              {chart.map((c) => <Cell key={c.full} fill={c.passed ? "#008C45" : "#E11D48"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Candidato</TableHead>
            {ov.tests.map((t) => <TableHead key={t.id}>{t.name}</TableHead>)}
            <TableHead>Media</TableHead><TableHead>Sostenuti</TableHead><TableHead>Esito complessivo</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {ov.rows.map((r) => (
              <TableRow key={r.enrollment_id} data-testid={`overall-row-${r.enrollment_id}`}>
                <TableCell className="font-medium">{r.name}</TableCell>
                {r.pcts.map((p, i) => <TableCell key={ov.tests[i].id} className={`tabular ${p === null ? "text-slate-400" : ""}`}>{p === null ? "non sostenuto" : `${p}%`}</TableCell>)}
                <TableCell className="tabular font-semibold">{r.avg_pct}%</TableCell>
                <TableCell className="tabular text-slate-500">{r.taken}/{r.tests}</TableCell>
                <TableCell><OutcomeBadge r={{ passed: r.passed }} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

export default function AssessmentsTab({ course }) {
  const [list, setList] = useState([]);
  const [version, setVersion] = useState(0);
  const [res, setRes] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const load = () => { setVersion((v) => v + 1); return api.get(`/courses/${course.id}/assessments`).then((r) => setList(r.data)); };
  useEffect(() => { load(); }, [course.id]); // eslint-disable-line
  const del = async () => {
    await api.delete(`/assessments/${toDelete.id}`); toast.success("Verifica eliminata"); setToDelete(null); load();
  };
  return (
    <div className="space-y-5">
      <UploadForm courseId={course.id} onDone={(d) => { setRes(d); load(); }} />
      <OverallSection course={course} version={version} />
      {list.length > 0 && <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider pt-2">Dettaglio per singolo test</h3>}
      {list.length === 0 ? (
        <Card className="p-8 border-dashed border-slate-300 text-center text-slate-500" data-testid="assessments-empty">
          <ClipboardCheck size={22} className="mx-auto mb-2 text-slate-400" /> Nessuna verifica caricata per questo corso.
        </Card>
      ) : list.map((a) => <AssessmentCard key={a.id} a={a} onChanged={load} onDelete={setToDelete} />)}
      <ImportResult res={res} onClose={() => setRes(null)} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina verifica</AlertDialogTitle>
            <AlertDialogDescription>Eliminare il test "{toDelete?.name}" e tutti i relativi risultati?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-rose-600 hover:bg-rose-700" data-testid="assessment-delete-confirm">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
